import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, CATEGORIES, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "music", headline: "", bio: "", location: "",
    cover_image: "", instagram: "", twitter: "", website: "", tags: "",
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/submissions/mine").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/profiles").then((r) => setProfiles(r.data.filter((p) => p.owner_id === user.user_id))).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="min-h-[50vh] grid place-items-center text-white/40 label">Loading…</div>;

  const createProfile = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      const { data } = await api.post("/profiles", payload);
      toast.success("Profile created");
      setProfiles([data, ...profiles]);
      setShowProfileForm(false);
      setForm({ name: "", category: "music", headline: "", bio: "", location: "", cover_image: "", instagram: "", twitter: "", website: "", tags: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const inp = "w-full bg-ink-700 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold";

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="label text-pfc-gold mb-3">Member Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-[0.9]">Welcome, <span className="gold-text">{user.name?.split(" ")[0] || "Creator"}</span></h1>
          <p className="text-white/60 mt-2 font-body">{user.email}</p>
        </div>
        <Link to="/submit" className="font-sub uppercase tracking-[0.2em] text-xs px-6 py-3 border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold transition" data-testid="dashboard-submit-link">+ Submit Story</Link>
        <Link to="/settings" className="font-sub uppercase tracking-[0.2em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition" data-testid="dashboard-settings-link">Settings · Privacy · 2FA · Monetization</Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="pfc-card p-8" data-testid="dashboard-profiles">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl text-white uppercase">My <span className="gold-text">Profiles</span></h2>
            <button onClick={() => setShowProfileForm(!showProfileForm)} className="label text-pfc-gold hover:text-pfc-gold-bright" data-testid="toggle-create-profile">
              {showProfileForm ? "Cancel" : "+ Create"}
            </button>
          </div>
          {showProfileForm ? (
            <form onSubmit={createProfile} className="space-y-3" data-testid="create-profile-form">
              <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input required placeholder="Headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={inp} />
              <textarea required rows={4} placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={inp} />
              <input placeholder="Location (City, Country)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inp} />
              <input placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inp} />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Instagram @handle" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={inp} />
                <input placeholder="Twitter @handle" value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} className={inp} />
              </div>
              <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inp} />
              <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inp} />
              <button type="submit" disabled={creating} className="w-full font-sub uppercase tracking-[0.25em] text-xs py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50" data-testid="create-profile-submit">
                {creating ? "Creating…" : "Create Profile"}
              </button>
            </form>
          ) : profiles.length === 0 ? (
            <p className="text-white/50 font-body text-sm">No profiles yet. Create your first one to appear in the public directory.</p>
          ) : (
            <ul className="space-y-3">
              {profiles.map((p) => (
                <li key={p.profile_id} className="flex items-center gap-3 p-3 border border-white/5">
                  <img src={p.cover_image} alt={p.name} className="w-14 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sub uppercase text-white">{p.name}</p>
                    <p className="text-white/50 text-xs truncate font-body">{p.headline}</p>
                  </div>
                  <Link to={`/profile/${p.profile_id}`} className="label text-pfc-gold">View →</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pfc-card p-8" data-testid="dashboard-submissions">
          <h2 className="font-display text-2xl text-white uppercase mb-6">My <span className="gold-text">Submissions</span></h2>
          {subs.length === 0 ? (
            <p className="text-white/50 font-body text-sm">You haven’t submitted any stories yet.</p>
          ) : (
            <ul className="space-y-3">
              {subs.map((s) => (
                <li key={s.submission_id} className="p-4 border border-white/5">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="font-sub uppercase text-white text-sm">{s.headline}</p>
                    <span className="label text-pfc-gold">{s.status}</span>
                  </div>
                  <p className="text-white/40 text-xs font-body">{s.category} · {new Date(s.created_at).toLocaleDateString()}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
